import type { Mark, Point } from "./schema";
import { angleDegrees, distance, polygonArea } from "./geometry";
export type MeasurementUnit = "m" | "cm" | "mm";
export const unitsPerMeter = { m: 1, cm: 100, mm: 1000 };
export function validColor(value: unknown, fallback = "#ffffff") {
  return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value)
    ? value
    : fallback;
}
export function boundedNumber(
  value: unknown,
  fallback: number,
  min: number,
  max: number,
) {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(min, Math.min(max, value))
    : fallback;
}
export function measurementLabel(shape: Mark, meters: number) {
  const { points, kind } = shape.props;
  if (shape.meta.polygon) return "";
  const precision = Math.round(
    boundedNumber(shape.meta.precision, kind === "angle" ? 1 : 2, 0, 4),
  );
  if (kind === "angle")
    return `${(shape.meta.reflex ? 360 - angleDegrees(points) : angleDegrees(points)).toFixed(precision)}°`;
  if (!meters) return "Set scale";
  const unit: MeasurementUnit =
    shape.meta.unit === "mm" || shape.meta.unit === "cm"
      ? shape.meta.unit
      : "m";
  const factor = meters * unitsPerMeter[unit];
  return kind === "area"
    ? `${(polygonArea(points) * factor ** 2).toFixed(precision)} ${unit}²`
    : `${(distance(points[0], points[points.length - 1]) * factor).toFixed(precision)} ${unit}`;
}
export function markPresentation(shape: Mark) {
  const { points: p, kind, weight } = shape.props;
  const a = p[0],
    b = p[p.length - 1],
    d = distance(a, b) || 1;
  const normal = { x: -(b.y - a.y) / d, y: (b.x - a.x) / d };
  const offset = boundedNumber(shape.meta.labelOffset, -12, -200, 200);
  const mid =
    kind === "area"
      ? {
          x: p.reduce((sum, q) => sum + q.x, 0) / p.length,
          y: p.reduce((sum, q) => sum + q.y, 0) / p.length,
        }
      : { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  let label = { x: mid.x + normal.x * offset, y: mid.y + normal.y * offset };
  const decorations: Point[][] = [];
  if (kind === "dimension")
    for (const [i, q] of [a, b].entries()) {
      if (shape.meta.endStyle === "arrow") {
        const sign = i ? -1 : 1,
          u = { x: ((b.x - a.x) / d) * sign, y: ((b.y - a.y) / d) * sign };
        decorations.push([
          {
            x: q.x + u.x * 10 + normal.x * 4,
            y: q.y + u.y * 10 + normal.y * 4,
          },
          q,
          {
            x: q.x + u.x * 10 - normal.x * 4,
            y: q.y + u.y * 10 - normal.y * 4,
          },
        ]);
      } else if (shape.meta.endStyle !== "none") {
        const tick =
          shape.meta.endStyle === "bar"
            ? normal
            : { x: normal.x + 0.7, y: normal.y + 0.7 };
        decorations.push([
          { x: q.x - tick.x * 7, y: q.y - tick.y * 7 },
          { x: q.x + tick.x * 7, y: q.y + tick.y * 7 },
        ]);
      }
    }
  if (kind === "angle" && p.length >= 3) {
    const center = p[1],
      start = Math.atan2(a.y - center.y, a.x - center.x);
    let sweep = Math.atan2(b.y - center.y, b.x - center.x) - start;
    while (sweep > Math.PI) sweep -= Math.PI * 2;
    while (sweep < -Math.PI) sweep += Math.PI * 2;
    if (shape.meta.reflex) sweep += sweep > 0 ? -Math.PI * 2 : Math.PI * 2;
    const r = boundedNumber(shape.meta.arcRadius, 32, 8, 200);
    const count = Math.max(8, Math.ceil(Math.abs(sweep) * 12));
    decorations.push(
      Array.from({ length: count + 1 }, (_, i) => ({
        x: center.x + Math.cos(start + (sweep * i) / count) * r,
        y: center.y + Math.sin(start + (sweep * i) / count) * r,
      })),
    );
    label = {
      x: center.x + Math.cos(start + sweep / 2) * (r + Math.abs(offset) + 12),
      y: center.y + Math.sin(start + sweep / 2) * (r + Math.abs(offset) + 12),
    };
  }
  return {
    label,
    decorations,
    dash:
      shape.meta.dash === "dashed"
        ? [weight * 6, weight * 3]
        : shape.meta.dash === "dotted"
          ? [weight, weight * 3]
          : [],
    fillColor: validColor(shape.meta.fillColor, shape.props.color),
    spacing: boundedNumber(shape.meta.hatchSpacing, 9, 3, 60),
    angle: boundedNumber(shape.meta.hatchAngle, 45, 0, 180),
  };
}
